import React from 'react';

import CheckIcon from '@mui/icons-material/Check';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Button, Chip, Collapse, Paper, Stack, Typography } from '@mui/material';

import { CHECKLIST_STATUS_STYLES } from '../constants';
import type { ManualApprovalState } from '../types';
import type { TaxesPaymentsChecklistRow } from '../utils';

import HiddenApprovedFooter from './HiddenApprovedFooter';

interface Props {
  expandedRows: Record<string, boolean>;
  hiddenApprovedCount: number;
  manualApprovals: ManualApprovalState;
  rows: Array<TaxesPaymentsChecklistRow>;
  showAutoApproved: boolean;
  onApprovalChange: (rowId: string) => void;
  onToggleDetails: (rowId: string) => void;
  onUnapprovalChange: (rowId: string) => void;
}

export default function TaxesPaymentsReportCard({
  expandedRows,
  hiddenApprovedCount,
  manualApprovals,
  rows,
  showAutoApproved,
  onApprovalChange,
  onToggleDetails,
  onUnapprovalChange,
}: Props) {
  return (
    <Stack spacing={1.5}>
      {rows.length === 0 ? (
        <Paper variant="outlined" sx={{ color: 'text.secondary', p: 4, textAlign: 'center' }}>
          No Taxes &amp; Payments rows match this view.
        </Paper>
      ) : (
        rows.map((row) => {
          const manuallyApproved = !!manualApprovals[row.id];
          const approved = row.status === 'approved' || manuallyApproved;
          const expanded = !!expandedRows[row.id];

          const cardSx = approved
            ? { bgcolor: 'success.50', borderColor: 'success.light' }
            : row.status === 'blocked'
              ? { bgcolor: 'grey.50', borderColor: 'grey.300' }
              : CHECKLIST_STATUS_STYLES.approvalQueueCard;

          const chipColor = approved ? 'success' : row.status === 'blocked' ? 'default' : 'warning';
          const chipLabel = manuallyApproved
            ? 'Manually approved'
            : row.status === 'approved'
              ? 'Auto-approved'
              : row.status === 'blocked'
                ? 'Missing data'
                : 'Requires approval';

          return (
            <Paper key={row.id} variant="outlined" sx={{ borderRadius: 1.25, overflow: 'hidden', ...cardSx }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 2 }}>
                {approved ? (
                  <CheckCircleOutlineIcon color="success" />
                ) : row.status === 'blocked' ? (
                  <ErrorOutlineIcon color="disabled" />
                ) : (
                  <WarningAmberIcon color="warning" />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
                    <Typography variant="subtitle2" fontWeight={700}>
                      {row.name}
                    </Typography>
                    <Chip color={chipColor} label={chipLabel} size="small" variant="outlined" />
                  </Stack>
                </Box>
                <Button
                  color="inherit"
                  size="small"
                  endIcon={expanded ? <ExpandLessIcon /> : <KeyboardArrowDownIcon />}
                  onClick={() => onToggleDetails(row.id)}
                >
                  {expanded ? 'Hide details' : 'View details'}
                </Button>
                {manuallyApproved && (
                  <Button size="small" variant="outlined" color="error" onClick={() => onUnapprovalChange(row.id)}>
                    Unapprove
                  </Button>
                )}
                {!approved && row.status !== 'blocked' && !manuallyApproved && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<CheckIcon />}
                    onClick={() => onApprovalChange(row.id)}
                  >
                    Approve
                  </Button>
                )}
              </Stack>
              <Collapse in={expanded}>
                <Box sx={{ bgcolor: 'grey.50', borderTop: 1, borderColor: 'divider', p: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Rule
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {row.rule}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Data points
                  </Typography>
                  <Stack
                    spacing={0}
                    sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}
                  >
                    {row.details.map((detail) => (
                      <Stack
                        key={detail.label}
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        sx={{
                          borderBottom: 1,
                          borderColor: 'divider',
                          p: 1.5,
                          '&:last-child': { borderBottom: 0 },
                        }}
                      >
                        <Typography variant="body2" color="text.secondary" sx={{ flex: '0 0 220px' }}>
                          {detail.label}
                        </Typography>
                        <Typography variant="body2">{detail.value}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              </Collapse>
            </Paper>
          );
        })
      )}
      <HiddenApprovedFooter
        count={hiddenApprovedCount}
        noun="Taxes & Payments rule"
        showAutoApproved={showAutoApproved}
      />
    </Stack>
  );
}
